/**
 * The wire contract between the Worker API and the SPA.
 *
 * Both tsconfigs include `src/shared`, so these types are the single source of
 * truth for request/response shapes without the SPA having to compile Worker
 * code (which targets workerd, not the DOM).
 */

import type { TemplateConfig } from './theme';

/**
 * Nepali literary taxonomy.
 *
 * Two levels: a reader browses by category, a writer files under a form. The
 * platform previously had a flat poem/story/essay, which cannot tell a गजल from
 * a मुक्तक or a संस्मरण from a समालोचना — distinctions this literature actually
 * makes and that a writer expects to declare.
 *
 * `category` is stored alongside `form` rather than derived at query time, so
 * feeds can filter and index on it directly.
 */
export const LITERARY_CATEGORIES = ['verse', 'fiction', 'drama', 'nonfiction', 'critique'] as const;
export type LiteraryCategory = (typeof LITERARY_CATEGORIES)[number];

export const LITERARY_FORMS = [
  // काव्य
  'laghu-kavita',
  'ghazal',
  'muktak',
  'geet',
  'kavita',
  'khandakavya',
  'mahakavya',
  // आख्यान
  'laghu-katha',
  'katha',
  'upanyas',
  // नाटक
  'ekanki',
  'natak',
  // गैर-आख्यान
  'nibandha',
  'prabandha',
  'samsmaran',
  'yatrasahitya',
  'jeevani',
  'atmakatha',
  // व्यङ्ग्य र समालोचना
  'repotaj',
  'samalochana',
  'byangya',
] as const;
export type LiteraryForm = (typeof LITERARY_FORMS)[number];

export interface FormMeta {
  np: string;
  en: string;
  category: LiteraryCategory;
  /** Shown under the picker so a writer can tell neighbouring forms apart. */
  hint: string;
}

export const FORM_META: Record<LiteraryForm, FormMeta> = {
  'laghu-kavita': { np: 'लघु-कविता', en: 'Laghu-kavita', category: 'verse', hint: 'छोटो, चोटिलो कविता' },
  ghazal: { np: 'गजल', en: 'Ghazal', category: 'verse', hint: 'रदिफ–काफियासहितका शेर' },
  muktak: { np: 'मुक्तक', en: 'Muktak', category: 'verse', hint: 'चार हरफको स्वतन्त्र कविता' },
  geet: { np: 'गीत', en: 'Geet', category: 'verse', hint: 'सङ्गीतका लागि लेखिएको' },
  kavita: { np: 'कविता', en: 'Kavita', category: 'verse', hint: 'छन्द वा गद्य कविता' },
  khandakavya: { np: 'खण्डकाव्य', en: 'Khandakavya', category: 'verse', hint: 'एउटै प्रसङ्गको आख्यानात्मक काव्य' },
  mahakavya: { np: 'महाकाव्य', en: 'Mahakavya', category: 'verse', hint: 'सर्गबद्ध विशाल काव्य' },

  'laghu-katha': { np: 'लघुकथा', en: 'Laghu-katha', category: 'fiction', hint: 'केही सय शब्दको कथा' },
  katha: { np: 'कथा', en: 'Katha', category: 'fiction', hint: 'एकल कथानकको छोटो कथा' },
  upanyas: { np: 'उपन्यास', en: 'Upanyas', category: 'fiction', hint: 'बहुस्तरीय पूर्ण उपन्यास' },

  ekanki: { np: 'एकाङ्की', en: 'Ekanki', category: 'drama', hint: 'एउटै अङ्कको नाटक' },
  natak: { np: 'नाटक', en: 'Natak', category: 'drama', hint: 'बहु-अङ्कीय पूर्ण नाटक' },

  nibandha: { np: 'निबन्ध', en: 'Nibandha', category: 'nonfiction', hint: 'विश्लेषणात्मक निबन्ध' },
  prabandha: { np: 'प्रबन्ध', en: 'Prabandha', category: 'nonfiction', hint: 'शोधपरक प्राज्ञिक लेख' },
  samsmaran: { np: 'संस्मरण', en: 'Samsmaran', category: 'nonfiction', hint: 'भोगाइका सम्झना' },
  yatrasahitya: { np: 'यात्रासाहित्य', en: 'Yatrasahitya', category: 'nonfiction', hint: 'यात्रा वर्णन' },
  jeevani: { np: 'जीवनी', en: 'Jeevani', category: 'nonfiction', hint: 'अर्कैले लेखेको जीवनकथा' },
  atmakatha: { np: 'आत्मकथा', en: 'Atmakatha', category: 'nonfiction', hint: 'आफैंले लेखेको जीवनकथा' },

  repotaj: { np: 'रिपोर्ताज', en: 'Repotaj', category: 'critique', hint: 'साहित्यिक पत्रकारिता' },
  samalochana: { np: 'समालोचना', en: 'Samalochana', category: 'critique', hint: 'कृतिको समीक्षा' },
  byangya: { np: 'व्यङ्ग्य', en: 'Byangya', category: 'critique', hint: 'व्यङ्ग्य र हास्य' },
};

export const CATEGORY_META: Record<LiteraryCategory, { np: string; en: string; forms: LiteraryForm[] }> = {
  verse: {
    np: 'काव्य',
    en: 'Verse',
    forms: ['kavita', 'ghazal', 'muktak', 'geet', 'laghu-kavita', 'khandakavya', 'mahakavya'],
  },
  fiction: { np: 'आख्यान', en: 'Fiction', forms: ['katha', 'laghu-katha', 'upanyas'] },
  drama: { np: 'नाटक', en: 'Drama', forms: ['natak', 'ekanki'] },
  nonfiction: {
    np: 'गैर-आख्यान',
    en: 'Non-fiction',
    forms: ['nibandha', 'samsmaran', 'yatrasahitya', 'jeevani', 'atmakatha', 'prabandha'],
  },
  critique: { np: 'समालोचना', en: 'Criticism', forms: ['samalochana', 'byangya', 'repotaj'] },
};

const FALLBACK_FORM: LiteraryForm = 'kavita';

/**
 * Metadata for a form, tolerating values this build does not know.
 *
 * `posts.form` is plain text so the taxonomy can grow without a table rebuild —
 * which means a row can legitimately carry a form an older or newer bundle has
 * never heard of. Indexing FORM_META directly would return undefined and take
 * the whole feed down on `.np`; falling back keeps the page up with a slightly
 * wrong label, which is the right trade.
 */
export function formMeta(form: string): FormMeta {
  return FORM_META[form as LiteraryForm] ?? FORM_META[FALLBACK_FORM];
}

export function categoryOf(form: string): LiteraryCategory {
  return formMeta(form).category;
}

/**
 * The one address a piece is shared as.
 *
 * Every post is readable at two URLs — the writer's own site and the platform's
 * mirror — and sharing whichever one the reader happened to be on would split a
 * single poem's likes and comments across two Facebook objects, and its ranking
 * across two search-engine documents. The writer's site wins because that is the
 * address that survives them outgrowing us.
 *
 * Server and client both call this, so the `og:url` in the head and the link in
 * the share sheet can never drift apart.
 */
export function canonicalPostUrl(authorSiteUrl: string, slug: string): string {
  return `${authorSiteUrl}/p/${encodeURIComponent(slug)}`;
}

/** Verse is laid out as verse — stanza keymaps, preserved line breaks. */
export function isVerse(category: LiteraryCategory): boolean {
  return category === 'verse';
}

/**
 * A writer's note on a stretch of their own text.
 *
 * The anchor lives in the body HTML as `<span class="annot" id="…">`, and the
 * note itself lives here rather than inside that span. Two reasons, both about
 * what the body HTML already has to survive: `htmlToText` feeds the excerpt and
 * the word count, and a note nested in the text would be counted as part of the
 * poem; and `splitPages` slices the body on page markers, which would cut a note
 * in half if the writer put a page break between the anchor and its text.
 *
 * The cost of the split is that ids must be kept in step — see
 * `pruneAnnotations`, which is the only thing allowed to decide what survives a
 * save.
 */
export interface Annotation {
  /** Matches the `id` of the anchoring span. See `ANNOTATION_ID_RE`. */
  id: string;
  /** Sanitised HTML — the writer gets emphasis and links inside a note. */
  html: string;
}

/**
 * The shape an annotation id must take.
 *
 * Narrow on purpose. `id` is the one attribute a writer can put on a span, and
 * an unconstrained one is a page-breaking tool at best (`id="root"`) and DOM
 * clobbering at worst. Nothing outside this pattern survives sanitising.
 */
export const ANNOTATION_ID_RE = /^an[a-z0-9]{6,20}$/;

/** Per post. A piece needing more notes than this is a piece with a preface. */
export const MAX_ANNOTATIONS = 200;
/** Per note, after sanitising. Long enough for a paragraph and a link. */
export const MAX_ANNOTATION_BYTES = 4_000;
/** The whole "behind the scenes" note. */
export const MAX_AUTHOR_NOTE_BYTES = 20_000;

export type PostStatus = 'draft' | 'published';

/**
 * How the front page is ordered.
 *
 * The platform reads as a monthly edition, so `popular` — most-read over the
 * last 30 days — is the default rather than reverse-chronological. A piece
 * published three weeks ago that people are still reading belongs above one
 * published this morning that nobody has opened.
 */
export const FEED_SORTS = ['popular', 'recent', 'trending', 'alltime'] as const;
export type FeedSort = (typeof FEED_SORTS)[number];

export const FEED_SORT_LABELS: Record<FeedSort, { np: string; en: string; hint: string }> = {
  popular: { np: 'यो महिनाका लोकप्रिय', en: 'Popular this month', hint: 'पछिल्लो ३० दिनमा सबैभन्दा धेरै पढिएका' },
  recent: { np: 'ताजा', en: 'Latest', hint: 'भर्खरै प्रकाशित' },
  trending: { np: 'चर्चित', en: 'Trending', hint: 'यो हप्ता चर्चामा रहेका' },
  alltime: { np: 'सर्वाधिक पठित', en: 'All time', hint: 'सुरुदेखि अहिलेसम्म' },
};

/** Mirrors Cloudflare's custom hostname lifecycle, plus a local `none`. */
export type DomainStatus =
  | 'none'
  | 'pending'
  | 'pending_validation'
  | 'active'
  | 'active_redeploying'
  | 'moved'
  | 'deleted'
  | 'blocked'
  | 'error';

export type UserRole = 'writer' | 'admin';
export type UserStatus = 'active' | 'suspended';

/**
 * Design tokens a site theme controls.
 *
 * The vocabulary lives in `@shared/theme` — the Worker validates against it and
 * the SPA renders from it, so neither can drift from the other. Re-exported
 * here because `TemplateConfig` is part of the wire contract like everything
 * else in this file.
 */
export type { TemplateConfig };

export interface Template {
  id: string;
  key: string;
  name: string;
  nameNp: string;
  description: string | null;
  config: TemplateConfig;
  isEnabled: boolean;
  isDefault: boolean;
  sortOrder: number;
}

export interface Invite {
  id: string;
  code: string;
  email: string | null;
  handle: string | null;
  displayName: string | null;
  note: string | null;
  url: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  /** When the invitation email went out, or null if it was never sent. */
  sentAt: string | null;
  createdAt: string;
  /** Derived so the UI does not re-implement expiry logic. */
  state: 'pending' | 'accepted' | 'expired' | 'revoked';
  acceptedBy: { handle: string; displayName: string } | null;
}

/**
 * A reading that enough different people chose to be worth serving.
 *
 * `votes` is distinct contributors, not total picks — see the schema note. The
 * client decides what to do with it, because the thresholds are a client-side
 * ranking decision and the server should not have to be redeployed to change
 * one.
 */
export interface CrowdPick {
  roman: string;
  text: string;
  votes: number;
}

/**
 * How much agreement it takes for the crowd to move what everyone gets.
 *
 * Shared so the API's cut-off and the client's rule are the same number, and so
 * they are written down once with the reason attached.
 */
export const CROWD_RULES = {
  /**
   * Distinct people who must have chosen a reading before it is served at all.
   *
   * Low enough to be reachable for an ordinary word, high enough that one person
   * with several browsers cannot decide a spelling.
   */
  minVoters: 5,
  /**
   * How far ahead of the runner-up a reading must be to become the default.
   *
   * A genuine disagreement — half the people writing पानी and half पनि — should
   * leave the engine's answer alone and simply reorder the list. Only a lopsided
   * result means "the guess is wrong", rather than "this word is ambiguous".
   */
  dominance: 3,
  /** Rows a single contribution batch may carry. */
  maxBatch: 50,
} as const;

/**
 * An API key, as its owner sees it.
 *
 * `key` is present exactly once — in the response that created it. Only a hash
 * is stored, so it cannot be shown again, and the UI has to say so at the moment
 * it is displayed rather than leaving someone to discover it later.
 */
export interface ApiKey {
  id: string;
  name: string;
  /** The leading, non-secret part, for telling keys apart in a list. */
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** Requests and words today, against the daily allowance. */
  today: { requests: number; words: number };
  /** Only on creation. Never retrievable afterwards. */
  key?: string;
}

/**
 * What a free key may convert per day, in words.
 *
 * Generous enough that no ordinary site will meet it — an embed never calls the
 * batch endpoint at all, since typing happens in the browser — and low enough
 * that a runaway script is capped rather than unbounded.
 */
export const FREE_DAILY_WORDS = 50_000;

/** One day in a time series. */
export interface DailyPoint {
  day: string;
  value: number;
}

export interface WriterAnalytics {
  range: { from: string; to: string; days: number };
  totals: { posts: number; published: number; drafts: number; views: number; likes: number };
  views: DailyPoint[];
  publications: DailyPoint[];
  topPosts: { id: string; title: string; slug: string; form: LiteraryForm; views: number; likes: number }[];
}

export interface AdminAnalytics {
  range: { from: string; to: string; days: number };
  totals: {
    writers: number;
    activeWriters: number;
    suspendedWriters: number;
    posts: number;
    published: number;
    views: number;
    likes: number;
    comments: number;
    customDomains: number;
    pendingInvites: number;
  };
  signups: DailyPoint[];
  publications: DailyPoint[];
  views: DailyPoint[];
  topPosts: { id: string; title: string; slug: string; handle: string; views: number; likes: number }[];
  topWriters: { id: string; handle: string; displayName: string; posts: number; views: number }[];
}

export interface AdminWriter extends PublicAuthor {
  email: string;
  role: UserRole;
  status: UserStatus;
  templateKey: string | null;
  domainStatus: DomainStatus;
  createdAt: string;
  postCount: number;
  publishedCount: number;
  viewCount: number;
}

export interface PublicAuthor {
  id: string;
  handle: string;
  displayName: string;
  penName: string | null;
  bio: string | null;
  avatarUrl: string | null;
  location: string | null;
  websiteUrl: string | null;
  customDomain: string | null;
  siteUrl: string;
  postCount?: number;
}

export interface Viewer extends PublicAuthor {
  email: string;
  role: UserRole;
  status: UserStatus;
  templateKey: string | null;
  /** Tokens this writer changed on top of their chosen theme. */
  themeOverrides: Partial<TemplateConfig> | null;
  /** Whether readers may comment anywhere on this writer's site. */
  commentsMode: CommentsMode;
  /** Whether their transliteration corrections feed the shared ranking. */
  contributesPicks: boolean;
  domainStatus: DomainStatus;
  /** When the address was proven. Null means unverified — recovery will not work. */
  emailVerifiedAt: string | null;
  createdAt: string;
}

export interface PostSummary {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  form: LiteraryForm;
  category: LiteraryCategory;
  status: PostStatus;
  excerpt: string;
  coverUrl: string | null;
  readingMinutes: number;
  wordCount: number;
  /** 1 for everything that has no page breaks, which is most posts. */
  pageCount: number;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  /**
   * Whether this piece takes comments — the writer's site-wide setting and the
   * post's own switch, already resolved. A reader should never be shown a form
   * that the server is going to refuse.
   */
  commentsOpen: boolean;
  publishedAt: string | null;
  updatedAt: string;
  /**
   * Held at the top of the writer's own site.
   *
   * False on the platform even for a piece that is pinned — not as a display
   * rule but as a fact about what was sent. A pin is something a writer does to
   * their own site, so kalampress.com is never told about one and cannot order
   * or mark by it. See `SerializeOptions.showPin`.
   */
  pinned: boolean;
  tags: string[];
  author: PublicAuthor;
  url: string;
}

/** A heading within a post, for the contents list. */
export interface OutlineHeading {
  id: string;
  text: string;
  /** 2 = section, 3 = subsection. */
  level: number;
}

export interface OutlinePage {
  page: number;
  /** First heading on the page, or its opening words. */
  label: string;
  headings: OutlineHeading[];
}

export interface Post extends PostSummary {
  /** Only the requested page — a महाकाव्य is not shipped whole to read page 3. */
  contentHtml: string;
  contentJson: unknown | null;
  /**
   * This post's own comment switch, before the site-wide setting is applied.
   *
   * The editor needs the switch itself; readers need `commentsOpen`, which is
   * the two settings already resolved. Showing the writer the resolved value
   * would make their own toggle look broken while the site setting is off.
   */
  commentsEnabled: boolean;
  likedByViewer?: boolean;
  /** 1-based page currently returned. */
  page: number;
  pageCount: number;
  /**
   * The whole document's structure, every page of it.
   *
   * Sent with each page because the reader's contents list has to show where
   * they are in the *piece*, not only in the page they happen to be on — and
   * shipping every page's HTML to build that client-side would defeat the point
   * of paginating at all.
   */
  outline: OutlinePage[];
  /** Where this reader left off, when the server knows. */
  progress?: ReadingProgress | null;
  /**
   * The writer's notes on the text, scoped to the page in `contentHtml`.
   *
   * Scoped rather than whole for the same reason the body is: a उपन्यास with two
   * hundred notes would ship all of them to read chapter one. The editor asks
   * for `wholeDocument` and gets every note back.
   */
  annotations: Annotation[];
  /**
   * What the writer wants to say about the piece rather than in it — what
   * prompted it, what it cost, what it nearly was.
   *
   * Sanitised HTML, null when the writer has written none. Shown after the text
   * and folded away until a reader asks for it: the piece is what they came for,
   * and an account of the piece is not the piece.
   */
  authorNoteHtml: string | null;
}

/**
 * A reader's place in a post.
 *
 * Kept per (reader, post) rather than per session: the point is that someone
 * returning next week — on a different device — lands where they stopped.
 */
export interface ReadingProgress {
  postId: string;
  page: number;
  /** 0–1 down the page, so a long page resumes mid-way rather than at its top. */
  percent: number;
  updatedAt: string;
}

/**
 * Whether a writer's site takes comments at all.
 *
 * Site-wide rather than per-post as the primary switch: a writer who does not
 * want a comment section does not want to turn one off twenty times, and one
 * who does will not want to remember to turn it on for every new piece. The
 * per-post switch (`Post.commentsEnabled`) narrows it, never widens it.
 */
export const COMMENTS_MODES = ['open', 'closed'] as const;
export type CommentsMode = (typeof COMMENTS_MODES)[number];

/**
 * Hidden comments are not deleted.
 *
 * A writer moderating their own readers is a delicate thing — most of what gets
 * hidden is a misunderstanding, not abuse — so hiding is reversible and the
 * commenter is never told. Deletion is separate and permanent.
 */
export type CommentStatus = 'visible' | 'hidden';

export interface Comment {
  id: string;
  postId: string;
  /** One level of nesting only: a reply to a reply attaches to the same thread. */
  parentId: string | null;
  body: string;
  status: CommentStatus;
  createdAt: string;
  /** Set when the comment was changed after posting; null when it never was. */
  editedAt: string | null;
  author: PublicAuthor;
  /**
   * What this viewer may do with it.
   *
   * Resolved server-side and sent down, rather than re-derived in the browser
   * from ids and roles. The rules — author edits their own, the post's writer
   * moderates their own page, an admin does anything — live in one place, and
   * the UI cannot offer a button the API will refuse.
   */
  canEdit: boolean;
  canDelete: boolean;
  canModerate: boolean;
  replies: Comment[];
}

export interface CommentThread {
  items: Comment[];
  /** Every visible comment on the post, replies included. */
  total: number;
  /** False when the writer has closed comments here or site-wide. */
  open: boolean;
}

/** A comment as it appears in the writer's moderation queue, with its post. */
export interface ModeratedComment extends Comment {
  post: { id: string; title: string; slug: string; url: string };
}

export interface Paginated<T> {
  items: T[];
  page: number;
  perPage: number;
  total: number;
  hasMore: boolean;
}

/** Resolved from the Host header — tells the SPA which site it is rendering. */
export interface SiteContext {
  siteName: string;
  appHost: string;
  rootDomain: string;
  /**
   * Which product this hostname is.
   *
   * `tool` is typeinnepali.com — the typing utility, which shares this Worker,
   * this bundle and the shared ranking with the publishing platform but is a
   * different site to a visitor.
   */
  kind: 'platform' | 'tool';
  /** Where the tool lives, for linking to it. Null when it has no domain yet. */
  toolHost: string | null;
  /** null on the platform host, an author on a tenant (custom domain / subdomain). */
  tenant: PublicAuthor | null;
  /** The theme to render this site with — the tenant's, or the platform default. */
  template: TemplateConfig | null;
  features: Features;
}

export interface Features {
  /**
   * Whether writers can attach their own domain right now.
   *
   * Cloudflare bills the platform per custom hostname per month, so this stays
   * off until there is a paid plan to charge against. Domains that are already
   * active keep serving either way — the flag gates new attachments, not
   * existing sites.
   */
  customDomains: boolean;
}

/**
 * One DNS record the writer has to create, and what public DNS says about it.
 *
 * The state is resolved server-side on every read rather than inferred from
 * Cloudflare'"'"'s overall verdict: "pending" for twenty minutes does not tell
 * someone which of three records they mistyped, and that is the only question
 * they actually have.
 */
export interface DomainRecord {
  /** routing = point the site here; ownership = prove it is yours; certificate = DCV. */
  purpose: 'routing' | 'ownership' | 'certificate';
  type: 'CNAME' | 'TXT';
  name: string;
  value: string;
  /** ok = found and matching · missing = nothing there · mismatch = wrong value · unknown = lookup failed. */
  state: 'ok' | 'missing' | 'mismatch' | 'unknown';
  /** What was found instead, when it differs. */
  found: string | null;
}

export interface DomainInfo {
  hostname: string | null;
  status: DomainStatus;
  /** Every record the writer must create, each with its live DNS state. */
  records: DomainRecord[];
  /**
   * True when Cloudflare has not yet issued the certificate (_acme-challenge)
   * record. It appears a moment after the hostname is created or switched to TXT
   * validation, and the page has to say so — a certificate step that is simply
   * absent reads as one that is not required.
   */
  certificatePending: boolean;
  sslStatus: string | null;
  errors: string[];
  checkedAt: string;
}

export interface ApiError {
  error: string;
  /** Field-level messages from zod, when validation failed. */
  issues?: Record<string, string[]>;
}

export interface UploadResult {
  key: string;
  url: string;
  size: number;
  contentType: string;
}
